import * as React from 'react';
import { globalHistory } from '@reach/router';
import { ErrorBoundary } from '@rollbar/react';
import { useClearAllErrors } from 'common/hooks/errors';
import Notification from 'common/components/Notification';
import Layout from 'common/components/Layout';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { accountDetailsSelector, currentStudyAtom, uiPreferencesSelector } from 'common/state/account';
import { StudyContext } from 'common/contexts/StudyContext';
import { useTracking } from 'common/hooks/tracking';
import { CREATE_CHART_HASH, CREATE_SEGMENT_HASH } from 'settings/constants';
import { useAppcues } from 'common/hooks/useAppcues';
import { brandsColorMapAtom } from 'common/state/brands';
import { generateBrandsColorMap } from 'utils/brands';
import { useRawSegments } from 'common/queries/segments';
import { ChartDashboardsContextProvider } from 'common/contexts/ChartsContext';
import { HistoryContextProvider } from 'common/contexts/HistoryContext';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from 'common/hooks/useOnAuthContext';
import { useGetAllCountries } from 'common/queries/countries';
import { useGetAllKpis } from 'common/queries/kpis';
import { segmentsColorsMapAtom } from 'common/state/segments';
import { generateSegmentsColorsMap } from 'utils/segments';
import { useGetAllBrands } from 'common/queries/brands';
import { useSeedChartStudyMapFromStudies } from 'common/queries/studies';

const RECOIL_HAS_VALUE = 'hasValue';

interface Props {
  children: React.ReactNode;
}

const Main = ({ children }: Props) => {
  const { loggedIn } = useAuthContext();
  const location = useLocation();
  const [{ studyId: currentStudy }] = useRecoilState(currentStudyAtom);
  const { data: segments, isLoading: isSegmentsLoading } = useRawSegments();
  const accountDetails = useRecoilValueLoadable(accountDetailsSelector);

  const { data: allBrandsList } = useGetAllBrands();
  const navigate = useNavigate();
  useSeedChartStudyMapFromStudies();
  useGetAllKpis();
  useGetAllCountries();

  useAppcues(accountDetails?.contents, currentStudy);

  const { state: accountDetailsState, contents: accountDetailsContent } = accountDetails;

  const setBrandsColorsMap = useSetRecoilState(brandsColorMapAtom);
  const setSegmentsColorsMap = useSetRecoilState(segmentsColorsMapAtom);
  const UIPreferences = useRecoilValue(uiPreferencesSelector);

  const { updateUserProperties } = useTracking();
  const { clearErrors } = useClearAllErrors();

  React.useEffect(() => {
    if (isSegmentsLoading) return;
    setSegmentsColorsMap(generateSegmentsColorsMap(segments));
  }, [setSegmentsColorsMap, segments, isSegmentsLoading]);

  React.useEffect(() => {
    if (accountDetailsState === RECOIL_HAS_VALUE) {
      updateUserProperties(accountDetailsContent);
    }
  }, [accountDetailsState, accountDetailsContent, updateUserProperties]);

  React.useEffect(() => {
    if (allBrandsList) {
      // Generate unique brands color map
      setBrandsColorsMap({
        ...generateBrandsColorMap(allBrandsList),
        ...UIPreferences.brandColors,
      });
    }
  }, [allBrandsList, setBrandsColorsMap, UIPreferences.brandColors]);

  // Remove alert box whenever page is changed
  React.useEffect(() => {
    return globalHistory.listen(() => {
      clearErrors();
    });
  }, [clearErrors]);

  const studyContext = React.useMemo(
    () => ({
      isSelectionModalOpen: [CREATE_SEGMENT_HASH, CREATE_CHART_HASH].includes(location?.hash as string),
      toggleStudySelectionModal: (state: boolean, hash = CREATE_CHART_HASH) => navigate(state ? hash : '#'),
    }),
    [location, navigate],
  );

  if (!loggedIn) {
    return <Navigate to='/login' />;
  }

  return (
    <StudyContext.Provider value={studyContext}>
      <ChartDashboardsContextProvider>
        <HistoryContextProvider>
          <Layout>
            <ErrorBoundary fallbackUI={() => <Notification />} errorMessage='Dashboard view error'>
              {children}
            </ErrorBoundary>
          </Layout>
        </HistoryContextProvider>
      </ChartDashboardsContextProvider>
    </StudyContext.Provider>
  );
};

export default React.memo(Main);
